// File: noviopus-ats-client/src/App/router/RouterWrapper/index.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useStore, useDispatch } from 'react-redux';

import Toastr from '@/components/Toastr';
import PromptDialog from '@/components/PromptDialog';
import { getAsyncInjectors, ExtendedStore } from '@/utils/asyncInjectors';
import styles from './RouterWrapper.module.scss';
import OrgSelectorDialog from '@/Pages/Recruitment/Job/OrgSelectorDialog';

// --- ROUTE LOADER HELPER ---
const RouteLoader = ({
  importer,
  name,
  inject,
  componentIndex = -1,
  componentProps = {},
}: {
  importer: () => Promise<any>;
  name: string;
  inject?: (modules: any, injectors: any) => void;
  componentIndex?: number;
  componentProps?: any;
}) => {
  const store = useStore();
  const [Component, setComponent] = useState<React.ComponentType | null>(null);

  const injectors = useMemo(() => getAsyncInjectors(store as ExtendedStore), [store]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const modules = await importer();

        if (!isMounted) return;

        if (inject) {
          inject(modules, {
            injectReducer: injectors.injectReducer,
            injectSagas: injectors.injectSagas,
          });
        }

        let Comp = null;
        if (Array.isArray(modules)) {
          if (componentIndex !== -1 && modules[componentIndex]) {
            Comp = modules[componentIndex];
          } else {
            // Fallback logic
            Comp = modules.find(
              (m) => m && (m.default || typeof m === 'function') && !m.toString().includes('function*'),
            );
            if (!Comp) Comp = modules[modules.length - 1];
          }
        } else {
          Comp = modules;
        }

        if (Comp && Comp.default) {
          Comp = Comp.default;
        }

        if (typeof Comp === 'object') {
          console.error(`[RouteLoader] WARNING: Component is an object! Check exports.`, Comp);
        }

        if (Comp && (typeof Comp === 'function' || typeof Comp === 'object')) {
                setComponent(() => Comp);
            } else {
                console.error(`[RouteLoader] Invalid component resolved for route: ${name}`, Comp);
            }
      } catch (err) {
        console.error(`[RouteLoader] Failed to load route ${name}`, err);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [name, componentIndex]);

  if (!Component) {
    return <div className="loader">Loading {name}...</div>;
  }

  return <Component {...componentProps} />;
};

const LocationSync = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({ type: '@@router/LOCATION_CHANGE', payload: { location, action: 'POP' } });
  }, [location, dispatch]);
  return null;
};

const LegacyRouteRenderer: React.FC<{ route: any }> = ({ route }) => {
  const [Comp, setComp] = useState<any>(null);
  useEffect(() => {
    let active = true;
    console.log(`[LegacyRouteRenderer] Loading legacy route: ${route.path}`);
    route.getComponent(null, (_: any, c: any) => {
      if (active) {
        console.log(`[LegacyRouteRenderer] Loaded component for ${route.path}:`, c);
        setComp(() => c);
      }
    });
    return () => {
      active = false;
    };
  }, [route]);

  if (!Comp) return null;
  return <Comp />;
};

export default function AppRoutes() {
  const featureFlags: { CANDIDATES_PAGE?: boolean; MESSAGES_PAGE?: boolean } =
    (process.env.REACT_APP_FEATURE_FLAGS as any) || {};
  const store = useStore();
  const injectors = useMemo(() => getAsyncInjectors(store as ExtendedStore), [store]);

  // Shared Injector Function
  const injectOrgModules = ([reducer, sagas]: any, { injectReducer, injectSagas }: any) => {
    injectReducer('organization', reducer.default);
    injectSagas(sagas.default);
  };

  const orgImporter = () =>
    Promise.all([
      import('@/features/Organization/organizationSlice'),
      import('@/features/Organization/sagas'),
      import('@/Pages/Management/Organization/OrganizationData/index'),
    ]);

  const orgMediaImporter = () =>
    Promise.all([
      import('@/features/Organization/organizationSlice'),
      import('@/features/Organization/sagas'),
      import('@/Pages/Management/Organization/OrganizationMedia/index'),
    ]);

  const orgWorkplaceImporter = () =>
    Promise.all([
      import('@/features/Organization/organizationSlice'),
      import('@/features/Organization/sagas'),
      import('@/Pages/Management/Organization/OrganizationWorkplace/index'),
    ]);

  const dashboardImporter = () =>
    Promise.all([
      import('@/features/Dashboard/dashboardSlice'),
      import('@/features/Dashboard/sagas'),
      import('@/Pages/Dashboard/DashboardContainer/index'),
    ]);

  const dashboardInjector = ([reducer, sagas]: any, { injectReducer, injectSagas }: any) => {
    injectReducer('dashboard', reducer.default);
    injectSagas(sagas.default);
  };

  // Job Importers
  const injectJobModules = ([jobFeature, orgFeature]: any, { injectReducer, injectSagas }: any) => {
    injectReducer('jobCreate', jobFeature.reducer);
    // FIX: Removed injectSagas(jobFeature.saga) from here!
    // It is already globally injected, so we don't want a second instance.
    if (orgFeature) {
      injectReducer('organization', orgFeature.reducer);
      injectSagas(orgFeature.saga);
    }
  };

  const jobBasicsImporter = () => Promise.all([import('@/features/Job'), import('@/Pages/Recruitment/Job/JobBasics')]);
  const jobBrandImporter = () =>
    Promise.all([import('@/features/Job'), import('@/features/Organization'), import('@/Pages/Recruitment/Job/JobBrand')]);
  const jobRequirementsImporter = () =>
    Promise.all([
      import('@/features/Job'),
      import('@/features/Organization'),
      import('@/Pages/Recruitment/Job/JobRequirements'),
    ]);
  const jobWorkflowImporter = () =>
    Promise.all([
      import('@/features/Job'),
      import('@/features/Organization'),
      import('@/Pages/Recruitment/Job/JobWorkflow'),
    ]);

  const hiringBoardImporter = () =>
    Promise.all([
      import('@/features/Hiring/slice'),
      import('@/features/Hiring/sagas'),
      import('@/Pages/Recruitment/Hiring/Board/index'),
    ]);

  const hiringListImporter = () =>
    Promise.all([
      import('@/features/Hiring/slice'),
      import('@/features/Hiring/sagas'),
      import('@/Pages/Recruitment/Hiring/List/index'),
    ]);

  const hiringInjector = ([reducer, sagas]: any, { injectReducer, injectSagas }: any) => {
    injectReducer('hiring', reducer.default);
    injectSagas(sagas.hiringBoardSaga); 
  };

  const candidatePageImporter = () =>
    Promise.all([
      import('@/features/CandidatePage/slice'),
      import('@/features/CandidatePage/sagas'),
      import('@/Pages/Recruitment/CandidatePage/index'),
    ]);

  const candidatePageInjector = ([slice, sagas]: any, { injectReducer, injectSagas }: any) => {
    injectReducer('candidateWorkspace', slice.default);
    injectSagas(sagas.candidateWorkspaceSaga);
  };

  const recruitmentDashboardImporter = () =>
    Promise.all([
      import('@/features/RecruitmentDashboard/slice'),
      import('@/features/RecruitmentDashboard/sagas'),
      import('@/Pages/Recruitment/Dashboard/index'),
    ]);

  const recruitmentDashboardInjector = ([reducer, sagas]: any, { injectReducer, injectSagas }: any) => {
    injectReducer('recruitmentDashboard', reducer.default);
    injectSagas(sagas.default);
  };

  const managementDashboardImporter = () =>
    Promise.all([
      import('@/features/ManagementDashboard/slice'),
      import('@/features/ManagementDashboard/sagas'),
      import('@/Pages/Management/Dashboard/index'),
    ]);

  const managementDashboardInjector = ([reducer, sagas]: any, { injectReducer, injectSagas }: any) => {
    injectReducer('managementDashboard', reducer.default);
    injectSagas(sagas.default);
  };


  return (
    <div className={styles.main}>
      <Helmet titleTemplate="Noviopus Business | %s" defaultTitle="Noviopus Business">
        <title>Noviopus Business</title>
        <meta name="description" content="Noviopus Business" />
      </Helmet>

      <LocationSync />

      <Routes>
        {/* ---------------- MODE 1: ONBOARDING ---------------- */}
        <Route
          path="/account/setup"
          element={<RouteLoader name="account-setup" importer={() => import('@/Pages/Account/AccountSetup/index')} />}
        />
        <Route path="/account/type" element={<Navigate to="/account/setup" replace />} />
        <Route
          path="/account/setup/edit"
          element={
            <RouteLoader
              name="account-setup-edit"
              importer={() => import('@/Pages/Account/AccountSetup/index')}
              componentProps={{ isOnboarding: false }}
            />
          }
        />

        <Route
          path="/organization/create/data"
          element={
            <RouteLoader name="org-onboard-data" importer={orgImporter} componentIndex={2} inject={injectOrgModules} />
          }
        />
        <Route
          path="/organization/create/media"
          element={
            <RouteLoader
              name="org-onboard-media"
              importer={orgMediaImporter}
              componentIndex={2}
              inject={injectOrgModules}
            />
          }
        />
        <Route
          path="/organization/create/workplace"
          element={
            <RouteLoader
              name="org-onboard-workplace"
              importer={orgWorkplaceImporter}
              componentIndex={2}
              inject={injectOrgModules}
            />
          }
        />
        <Route path="/organization/create" element={<Navigate to="/organization/create/data" replace />} />

        {/* ---------------- MODE 2: CREATE WIZARD (ID Based) ---------------- */}

        <Route
          path="/organization/new"
          element={
            <RouteLoader
              name="org-new-init"
              importer={orgImporter}
              componentIndex={2}
              inject={injectOrgModules}
              componentProps={{ isOnboarding: false }}
            />
          }
        />
        <Route
          path="/organization/:id/new/data"
          element={
            <RouteLoader
              name="org-new-data"
              importer={orgImporter}
              componentIndex={2}
              inject={injectOrgModules}
              componentProps={{ isOnboarding: false }}
            />
          }
        />
        <Route
          path="/organization/:id/new/media"
          element={
            <RouteLoader
              name="org-new-media"
              importer={orgMediaImporter}
              componentIndex={2}
              inject={injectOrgModules}
              componentProps={{ isOnboarding: false }}
            />
          }
        />
        <Route
          path="/organization/:id/new/workplace"
          element={
            <RouteLoader
              name="org-new-workplace"
              importer={orgWorkplaceImporter}
              componentIndex={2}
              inject={injectOrgModules}
              componentProps={{ isOnboarding: false }}
            />
          }
        />

        {/* ---------------- MODE 3: EDIT (SINGLE PAGE) ---------------- */}

        <Route
          path="/organization/:id/edit/data"
          element={
            <RouteLoader
              name="org-edit-data"
              importer={orgImporter}
              componentIndex={2}
              inject={injectOrgModules}
              componentProps={{ isOnboarding: false }}
            />
          }
        />
        <Route
          path="/organization/:id/edit/media"
          element={
            <RouteLoader
              name="org-edit-media"
              importer={orgMediaImporter}
              componentIndex={2}
              inject={injectOrgModules}
              componentProps={{ isOnboarding: false }}
            />
          }
        />
        <Route
          path="/organization/:id/edit/workplace"
          element={
            <RouteLoader
              name="org-edit-workplace"
              importer={orgWorkplaceImporter}
              componentIndex={2}
              inject={injectOrgModules}
              componentProps={{ isOnboarding: false }}
            />
          }
        />

        <Route
          path="/dashboard/management"
          element={
            <RouteLoader 
              name="management-dash" 
              importer={managementDashboardImporter} 
              inject={managementDashboardInjector} 
              componentIndex={2} 
            />
          }
        />

        {/* ---------------- MODE 4: JOB WIZARD (ID-BASED) ---------------- */}

        <Route
          path="/jobs/new/basics"
          element={
            <RouteLoader
              name="job-basics-new"
              importer={jobBasicsImporter}
              componentIndex={1}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/new/basics"
          element={
            <RouteLoader
              name="job-basics-new-id"
              importer={jobBasicsImporter}
              componentIndex={1}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/edit/basics"
          element={
            <RouteLoader
              name="job-basics-edit"
              importer={jobBasicsImporter}
              componentIndex={1}
              inject={injectJobModules}
            />
          }
        />

        <Route
          path="/jobs/new/brand"
          element={
            <RouteLoader
              name="job-brand-new"
              importer={jobBrandImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/new/brand"
          element={
            <RouteLoader
              name="job-brand-new-id"
              importer={jobBrandImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/edit/brand"
          element={
            <RouteLoader
              name="job-brand-edit"
              importer={jobBrandImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />

        <Route
          path="/jobs/new/requirements"
          element={
            <RouteLoader
              name="job-requirements-new"
              importer={jobRequirementsImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/new/requirements"
          element={
            <RouteLoader
              name="job-requirements-new-id"
              importer={jobRequirementsImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/edit/requirements"
          element={
            <RouteLoader
              name="job-requirements-edit"
              importer={jobRequirementsImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />

        <Route
          path="/jobs/new/workflow"
          element={
            <RouteLoader
              name="job-workflow-new"
              importer={jobWorkflowImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/new/workflow"
          element={
            <RouteLoader
              name="job-workflow-new-id"
              importer={jobWorkflowImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />
        <Route
          path="/jobs/:jobId/edit/workflow"
          element={
            <RouteLoader
              name="job-workflow-edit"
              importer={jobWorkflowImporter}
              componentIndex={2}
              inject={injectJobModules}
            />
          }
        />

        <Route path="/jobs/new" element={<Navigate to="/jobs/new/basics" replace />} />

        <Route
          path="/dashboard/recruitment"
          element={
            <RouteLoader
              name="recruitment-dash"
              importer={recruitmentDashboardImporter}
              inject={recruitmentDashboardInjector}
              componentIndex={2}
            />
          }
        />

        {/* The Kanban Board View */}
        <Route
          path="/jobs/job/:jobId/board"
          element={
            <RouteLoader
              name="hiring-board"
              importer={hiringBoardImporter}
              inject={hiringInjector}
              componentIndex={2}
            />
          }
        />

        {/* The Pipeline List View */}
        <Route
          path="/jobs/job/:jobId/candidates"
          element={
            <RouteLoader
              name="hiring-list"
              importer={hiringListImporter}
              inject={hiringInjector}
              componentIndex={2}
            />
          }
        />

        {/* SINGLE CANDIDATE WORKSPACE */}
        <Route
          path="/jobs/:jobId/candidates/:candidateId"
          element={
            <RouteLoader
              name="candidate-workspace"
              importer={candidatePageImporter}
              inject={candidatePageInjector}
              componentIndex={2}
            />
          }
        />

        {/* --- NEW: GAMIFICATION AWARDS DASHBOARD --- */}
        <Route
          path="/account/awards"
          element={
            <RouteLoader
              name="awards-dashboard"
              importer={() => import('@/Pages/Account/Awards/index')}
            />
          }
        />

        {/* --- LEGACY --- */}
        <Route path="/companies" element={<Navigate to="/dashboard/management" replace />} />

        <Route
          path="/dashboard"
          element={
            <RouteLoader name="dashboard" importer={dashboardImporter} inject={dashboardInjector} componentIndex={2} />
          }
        />
        <Route index element={<Navigate to="/dashboard" replace />} />

        <Route
          path="*"
          element={<RouteLoader name="notfound" importer={() => import('@/Errors/NotFoundPage/index')} />}
        />

        <Route
          path="/dashboard"
          element={
            <RouteLoader name="dashboard" importer={dashboardImporter} inject={dashboardInjector} componentIndex={2} />
          }
        />
        <Route index element={<Navigate to="/dashboard" replace />} />

        <Route
          path="*"
          element={<RouteLoader name="notfound" importer={() => import('@/Errors/NotFoundPage/index')} />}
        />

        <Route
          path="/dashboard"
          element={
            <RouteLoader name="dashboard" importer={dashboardImporter} inject={dashboardInjector} componentIndex={2} />
          }
        />
        <Route index element={<Navigate to="/dashboard" replace />} />

        <Route
          path="/adcampaigns"
          element={
            <RouteLoader
              name="campaigns-v3"
              importer={() => import('@/Pages/Recruitment/Campaigns/index')}
            />
          }
        />
  
        <Route
          path="/settings/team"
          element={
            <RouteLoader
              name="team-v3"
              importer={() => import('@/Pages/Account/Team/index')}
            />
          }
        />
        
        {/* REPLACED LEGACY ROUTE WITH V3 ROUTE */}
        <Route
          path="/settings/team/:userId"
          element={
            <RouteLoader
              name="team-member-v3"
              importer={() => import('@/Pages/Account/TeamMember/index')}
            />
          }
        />
        
        {/* --- NEW: 2-PAGE PLG BILLING HUB --- */}
        <Route path="/settings/billing" element={<Navigate to="/settings/billing/usage" replace />} />
        
        <Route
          path="/settings/billing/usage"
          element={
            <RouteLoader
              name="billing-usage"
              importer={() => import('@/Pages/Account/Billing/Usage/index')}
            />
          }
        />
        
        <Route
          path="/settings/billing/payments"
          element={
            <RouteLoader
              name="billing-payments"
              importer={() => import('@/Pages/Account/Billing/Payments/index')}
            />
          }
        />

        {/* --- NEW: PAY-AS-YOU-GO ACTIVATION PAYWALL --- */}
        <Route
          path="/settings/billing/activate"
          element={
            <RouteLoader
              name="billing-activate"
              importer={() => import('@/Pages/Account/Billing/Activate/index')}
            />
          }
        />
        <Route
          path="/reports"
          element={
            <RouteLoader
              name="reports-page"
              importer={() =>
                Promise.all([
                  import('@/Pages/Reports/reducer'),
                  import('@/Pages/Reports/sagas'),
                  import('@/Pages/Reports/ReportsPage/index'),
                ])
              }
              componentIndex={2}
              inject={([reducer, sagas], { injectReducer, injectSagas }) => {
                injectReducer('reports', reducer.default);
                injectSagas(sagas.default);
              }}
            />
          }
        />
        <Route
          path="/messages"
          element={
            featureFlags.MESSAGES_PAGE ? (
              <RouteLoader name="messages-page" importer={() => import('@/Pages/Messages/MessagesPage/index')} />
            ) : (
              <RouteLoader name="notfound" importer={() => import('@/Errors/NotFoundPage/index')} />
            )
          }
        />
        <Route
          path="/default"
          element={<RouteLoader name="default" importer={() => import('@/Pages/Account/DefaultPage/index')} />}
        />
        <Route
          path="*"
          element={<RouteLoader name="notfound" importer={() => import('@/Errors/NotFoundPage/index')} />}
        />
      </Routes>
      <Toastr />
      <PromptDialog />
      <OrgSelectorDialog />
    </div>
  );
}
